/** Knowledge Graph page — interactive Cytoscape.js visualization + AI insights */

import { useState, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Keyboard } from 'lucide-react'
import { useGraphData } from '../hooks/use-graph'
import { GraphCanvas, type GraphCanvasHandle } from '../components/graph/graph-canvas'
import { GraphToolbar, type GraphFilters } from '../components/graph/graph-toolbar'
import { GraphInsightPanel } from '../components/graph/graph-insight-panel'
import { GraphKeyboardHelp } from '../components/graph/graph-keyboard-help'
import { GraphMiniMap } from '../components/graph/graph-minimap'
import { evaluateSigmaMigrationGate, type GraphPerformanceSample } from '../components/graph/graph-performance-gate'
import { nextGraphSelection } from '../components/graph/graph-selection'

export function GraphPage() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<GraphFilters>({})
  const [selectedNodes, setSelectedNodes] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [keyboardHelpOpen, setKeyboardHelpOpen] = useState(false)
  const [performanceSample, setPerformanceSample] = useState<GraphPerformanceSample | null>(null)
  const { data, isLoading } = useGraphData(filters)
  const canvasRef = useRef<GraphCanvasHandle>(null)
  const keyboardButtonRef = useRef<HTMLButtonElement>(null)
  const sigmaGate = useMemo(
    () => performanceSample ? evaluateSigmaMigrationGate(performanceSample) : null,
    [performanceSample],
  )

  const handleNodeSelect = useCallback((nodeId: string) => {
    setSelectedNodes((prev) => nextGraphSelection(prev, nodeId))
  }, [])

  const handleNodeNavigate = useCallback((docId: string) => {
    const node = data?.nodes.find((n) => n.data.id === docId)
    if (node) navigate(`/doc/${node.data.slug}`)
  }, [data, navigate])

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
    if (!query.trim()) {
      canvasRef.current?.clearHighlight()
      return
    }
    canvasRef.current?.highlightSearch(query)
  }, [])

  const handleResetView = useCallback(() => {
    setSelectedNodes([])
    setSearchQuery('')
    canvasRef.current?.clearHighlight()
    canvasRef.current?.fitToView()
  }, [])

  const closeKeyboardHelp = useCallback(() => {
    setKeyboardHelpOpen(false)
    requestAnimationFrame(() => keyboardButtonRef.current?.focus())
  }, [])

  return (
    <div className="flex h-dvh flex-col bg-surface-0">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-white/[0.06] px-4 py-3">
        <button
          onClick={() => navigate('/')}
          className="text-neutral-400 hover:text-neutral-200"
          title="Back to editor"
          aria-label="Back to editor"
        >
          <ArrowLeft size={16} />
        </button>
        <h1 className="text-sm font-semibold text-neutral-200">Knowledge Graph</h1>
        {sigmaGate?.shouldReviewSigma && (
          <span
            className="rounded bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-300"
            title={`Sigma gate: ${sigmaGate.reasons.join(', ')}`}
          >
            Sigma review
          </span>
        )}
        {isLoading && (
          <div className="h-3.5 w-3.5 animate-spin rounded-full border border-neutral-600 border-t-brand-500" />
        )}
        <button
          ref={keyboardButtonRef}
          type="button"
          onClick={() => setKeyboardHelpOpen(true)}
          className="ml-auto flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 hover:bg-surface-2 hover:text-neutral-200"
          title="Keyboard shortcuts"
          aria-label="Keyboard shortcuts"
        >
          <Keyboard size={15} />
        </button>
      </div>

      {/* Toolbar with search + reset */}
      <GraphToolbar
        filters={filters}
        onChange={setFilters}
        stats={data?.stats}
        searchValue={searchQuery}
        onSearch={handleSearch}
        onResetView={handleResetView}
      />

      {/* Main content: graph canvas + insight panel */}
      <div className="flex flex-1 overflow-hidden">
        {data ? (
          <div className="relative flex min-w-0 flex-1">
            <GraphCanvas
              ref={canvasRef}
              data={data}
              onNodeSelect={handleNodeSelect}
              onNodeNavigate={handleNodeNavigate}
              onPerformanceSample={setPerformanceSample}
            />
            <GraphMiniMap data={data} selectedNodes={selectedNodes} />
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            {isLoading ? (
              <div className="flex flex-col items-center gap-2">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-700 border-t-brand-500" />
                <span className="text-xs text-neutral-500">Loading graph...</span>
              </div>
            ) : (
              <span className="text-sm text-neutral-600">No documents found</span>
            )}
          </div>
        )}
        <GraphInsightPanel
          selectedNodes={selectedNodes}
          onNavigate={handleNodeNavigate}
        />
      </div>
      <GraphKeyboardHelp open={keyboardHelpOpen} onClose={closeKeyboardHelp} />
    </div>
  )
}
